import { events } from "@constants/tracking";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import useAnalytics from "@hooks/useAnalytics";
import type { SessionItemsNavFilterValue } from "@memorang/ui/components/SessionItemsNavPane";
import { useDevice } from "@memorang/ui/useDevice";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo } from "react";
import {
	buildSessionItemsNavItems,
	getSessionItemsNavFiltersForMode,
	getSessionItemsNavProgress,
} from "../containers/StudySessionBottombarContainer/session-items-nav";

type StateSlice = {
	setShowSessionItemsNav: React.Dispatch<React.SetStateAction<boolean>>;
	sessionItemsNavSelectedFilters: SessionItemsNavFilterValue[];
	setSessionItemsNavSelectedFilters: React.Dispatch<
		React.SetStateAction<SessionItemsNavFilterValue[]>
	>;
};

export const useSessionItemsNavCore = ({
	setShowSessionItemsNav,
	sessionItemsNavSelectedFilters,
	setSessionItemsNavSelectedFilters,
}: StateSlice) => {
	const id = useSessionStore((s) => s.id);
	const inReviewMode = useSessionStore((s) => s.inReviewMode);
	const isSummativeTest = useSessionStore((s) => s.isSummativeTest);
	const itemStates = useSessionStore((s) => s.itemStates);
	const answers = useSessionStore((s) => s.answers);
	const blockStates = useSessionStore((s) => s.blockStates);
	const updateProgress = useSessionStore((s) => s.updateProgress);

	const { index } = useLocalSearchParams<{ index: string }>();
	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const { explicitCallTrackCustomEvent } = useAnalytics();
	const { isDesktopWeb } = useDevice();
	const { currentBlock, currentItem, currentBlockIndex } = useCurrentSession();
	const currentItemId = currentItem?.id!;

	const currentBlockState = blockStates.get(currentBlockIndex);
	const markedItems = currentBlockState?.markedItemsForReview;

	const sessionItemsNavItems = useMemo(
		() =>
			buildSessionItemsNavItems({
				items: currentBlock,
				itemStates,
				answers,
				markedItemIds: markedItems,
				inReviewMode,
			}),
		[answers, currentBlock, inReviewMode, itemStates, markedItems],
	);

	const sessionItemsNavFilters = useMemo(
		() => getSessionItemsNavFiltersForMode(inReviewMode),
		[inReviewMode],
	);

	const handleCloseSessionItemsNav = useCallback(() => {
		setShowSessionItemsNav(false);
	}, [setShowSessionItemsNav]);

	const handleOpenSessionItemsNav = useCallback(() => {
		if (!isSummativeTest) {
			return;
		}
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemsNavClicked,
			sessionId: id,
			itemId: currentItemId,
			inReviewMode: String(inReviewMode),
		});
		setShowSessionItemsNav(true);
	}, [
		currentItemId,
		explicitCallTrackCustomEvent,
		id,
		inReviewMode,
		isSummativeTest,
		setShowSessionItemsNav,
	]);

	const handleSessionItemsNavPress = useCallback(
		(_item: (typeof sessionItemsNavItems)[number], itemIndex: number) => {
			explicitCallTrackCustomEvent({
				eventName: events.sessionItemNavigated,
				sessionId: id,
				itemId: currentItemId,
				type: "jump",
				targetIndex: itemIndex + 1,
			});
			updateProgress(
				getSessionItemsNavProgress(itemIndex, currentBlock.length),
			);
			if (!isDesktopWeb) {
				setShowSessionItemsNav(false);
			}
			router.setParams({
				index: String(itemIndex + 1),
				prev: itemIndex < currentItemIndex ? "true" : "false",
			});
		},
		[
			currentBlock.length,
			currentItemId,
			currentItemIndex,
			explicitCallTrackCustomEvent,
			id,
			isDesktopWeb,
			setShowSessionItemsNav,
			updateProgress,
		],
	);

	useEffect(() => {
		if (!isSummativeTest) {
			setShowSessionItemsNav(false);
			setSessionItemsNavSelectedFilters([]);
		}
	}, [
		isSummativeTest,
		setSessionItemsNavSelectedFilters,
		setShowSessionItemsNav,
	]);

	return {
		isSummativeTest,
		sessionItemsNavItems,
		sessionItemsNavFilters,
		handleOpenSessionItemsNav,
		handleCloseSessionItemsNav,
		handleSessionItemsNavPress,
		questionsNavActiveFilterCount: sessionItemsNavSelectedFilters.length,
	};
};
